import { Spacing, Typography } from '@persona-id/design-tokens';
import { CircleHelpIcon } from '@persona-id/design-tokens/icons';
import { FC, useCallback, useState } from 'react';
import styled from 'styled-components';

import CodeEditor from 'dashboard/components-lib/CodeEditor';
import { jsonSchemaToEvalCodeReturnType } from 'dashboard/lib/workflows/util';
import { WorkflowRunSimulateContext } from 'dashboard/models/workflow-run-simulated';
import {
  WorkflowActionEvaluateCodeV2,
  WorkflowStepAction,
} from 'dashboard/models/workflow-version';
import { StepOverrideBaseProps } from 'dashboard/views/DashboardWorkflowRunsSimulateShowView/WorkflowSimulateControlPanel/StepOverride/StepOverrideBaseProps';
import Input from 'design-system/components/Input/Input';
import Radio from 'design-system/components/Radio';
import RadioGroup from 'design-system/components/RadioGroup';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';

const OverrideConfigContainer = styled.div`
  padding: ${Spacing[2]} ${Spacing[5]} ${Spacing[2]} ${Spacing[1]};
  flex: auto;
  overflow-y: auto;
  margin-top: 1px;
`;
const StyledText = styled.div`
  ${Typography.TextMediumBold};
`;

const TooltipContainer = styled.div`
  max-width: 200px;
`;

const RadioContainer = styled.div`
  display: flex;
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.color.variants.danger.default};
`;

const PLACEHOLDER_TEXT = `
{
  "keys": "must",
  "be": {
    "surrounded": ["by", "curly"],
    "braces": true
  }
}
`;
const getReturnType = (step: WorkflowStepAction) => {
  if ('return-type' in step.action.params) {
    return step.action.params['return-type'] as string;
  }

  if ('return-schema' in step.action.params) {
    const typedAction = step.action as WorkflowActionEvaluateCodeV2;
    return jsonSchemaToEvalCodeReturnType(
      typedAction.params['return-schema']['config']['json-schema'],
    );
  }
  return null;
};

const getResultOverride = (simulateContext: WorkflowRunSimulateContext, stepToken: string) => {
  const stepOverride = simulateContext.overrides?.steps?.[stepToken];
  if (stepOverride?.output && 'result' in stepOverride.output) {
    const result = stepOverride.output.result;
    return typeof result === 'object' ? JSON.stringify(result, null, '\t') : result.toString();
  }
  return null;
};

const OverrideEvaluateCode: FC<StepOverrideBaseProps> = ({
  step,
  setSimulateContext,
  simulateContext,
  disabled,
}) => {
  const initialResultOverride = getResultOverride(simulateContext, step.token);
  const [overrideStepResult, setOverrideStepResult] = useState(initialResultOverride != null);
  const [resultOverride, setResultOverride] = useState(initialResultOverride);
  const [hasError, setHasError] = useState(false);
  const [unformattedObjectResult, setUnformattedObjectResult] = useState(initialResultOverride);
  const returnType = getReturnType(step);

  const updateOverrideStepResult = (value: string) => {
    const overrideResult = value === 'true';
    setOverrideStepResult(overrideResult);
    setResultOverride(null);
    if (!overrideResult && simulateContext?.overrides?.steps?.[step.token]) {
      const { [step.token]: _, ...filteredSteps } = simulateContext.overrides.steps;
      setSimulateContext({
        ...simulateContext,
        overrides: {
          ...simulateContext.overrides,
          steps: filteredSteps,
        },
      });
    }
  };

  const overrideCodeResult = useCallback(
    (value: string | number | boolean | object) => {
      setSimulateContext({
        ...simulateContext,
        overrides: {
          ...simulateContext.overrides,
          steps: {
            ...simulateContext.overrides?.steps,
            [step.token]: {
              output: {
                result: value,
              },
            },
          },
        },
      });
      setResultOverride(
        typeof value === 'object' ? JSON.stringify(value, null, '\t') : value.toString(),
      );
    },
    [setSimulateContext, simulateContext, step.token],
  );
  return (
    <OverrideConfigContainer>
      <StyledText>Step results</StyledText>
      <Spacer height={1} />
      <RadioGroup id='override-evaluate-code__result-selection'>
        <Radio
          label='Use function result'
          value='false'
          onChange={() => updateOverrideStepResult('false')}
          checked={overrideStepResult.toString() === 'false'}
          disabled={disabled || returnType === 'void'}
        />
        <RadioContainer>
          <Radio
            label='Override with custom value'
            value='true'
            onChange={() => updateOverrideStepResult('true')}
            checked={overrideStepResult.toString() === 'true'}
            disabled={disabled || returnType === 'void'}
          />
          {returnType === 'void' && (
            <Tooltip
              side='top'
              aria-label='Override is not available for void return type.'
              iconTrigger={<CircleHelpIcon size={14} />}
            >
              <TooltipContainer>
                <Paragraph>Override is not available for void return type.</Paragraph>
              </TooltipContainer>
            </Tooltip>
          )}
        </RadioContainer>
      </RadioGroup>
      {overrideStepResult && (
        <>
          <Spacer height={1} />
          {(() => {
            switch (returnType) {
              case 'string':
                return (
                  <Input
                    id='override-evaluate-code__string-input'
                    value={resultOverride ?? ''}
                    disabled={disabled}
                    onChange={(e) => overrideCodeResult(e.target.value)}
                    aria-label='String result override'
                  />
                );
              case 'number':
                return (
                  <Input
                    id='override-evaluate-code__number-input'
                    value={resultOverride ?? ''}
                    disabled={disabled}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        overrideCodeResult(0);
                      } else {
                        overrideCodeResult(parseFloat(value));
                      }
                    }}
                    aria-label='Number result override'
                    type='number'
                  />
                );
              case 'boolean':
                return (
                  <Select
                    id='override-evaluate-code__boolean-selection'
                    value={resultOverride}
                    onValueChange={(value: string) => overrideCodeResult(value === 'true')}
                    aria-label='Boolean result override'
                    disabled={disabled}
                  >
                    <Option value='true'>true</Option>
                    <Option value='false'>false</Option>
                  </Select>
                );
              case 'object':
                return (
                  <>
                    <CodeEditor
                      aria-label='Custom object result override'
                      language='jsonjs'
                      code={unformattedObjectResult || ''}
                      placeholder={PLACEHOLDER_TEXT}
                      hasError={hasError}
                      onChange={(value) => {
                        try {
                          setUnformattedObjectResult(value);
                          overrideCodeResult(JSON.parse(value));
                          setHasError(false);
                        } catch (e) {
                          setHasError(true);
                        }
                      }}
                    />
                    {hasError && (
                      <ErrorMessage>JSON is invalid. Invalid JSON will not be saved.</ErrorMessage>
                    )}
                  </>
                );
              default:
                return <></>;
            }
          })()}
        </>
      )}
    </OverrideConfigContainer>
  );
};

export default OverrideEvaluateCode;
